"use client";

import { Menu } from "@base-ui/react/menu";
import { LogOut, Menu as MenuIcon } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";

import { TenantLogo } from "@/components/tenant-logo";
import { Button, buttonVariants } from "@/components/ui/button";
import { tenantLogoFor } from "@/config/tenant-assets";
import { tenantPresentationConfigurations } from "@/config/tenant-presentation";
import type { TenantIdentity } from "@/config/tenants";
import { logout } from "@/features/auth/services/auth-service";
import { useIdleLogout } from "@/features/auth/use-idle-logout";
import { dashboardCopy, type AppLocale } from "@/features/resume/content/dashboard-copy";
import type { User } from "@/types/auth";
import type { ResumeAccess } from "@/types/resume";

interface AuthenticatedHeaderProps {
  tenant: TenantIdentity;
  user: User | null;
  locale: AppLocale;
  onLocaleChange(locale: AppLocale): void;
  access?: ResumeAccess | null;
  onActivate?(): void;
}

export function AuthenticatedHeader({ tenant, user, locale, onLocaleChange, access = null, onActivate }: AuthenticatedHeaderProps) {
  const router = useRouter();
  const copy = dashboardCopy[locale];
  const initials = user
    ? user.name
        .split(/\s+/)
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "";
  const hasLogo = tenantLogoFor(tenant.code, locale) !== null;
  const usesStructure = tenantPresentationConfigurations[tenant.code] !== undefined;
  const showsAccessReminder = tenant.code === "12connex";
  const guideHref = tenant.code === "12connex" ? (locale === "fr" ? "/12connex/aide" : "/12connex/help") : null;
  const [now] = useState(() => Date.now());
  const daysRemaining = access?.paid_expires_at
    ? Math.max(0, Math.ceil((new Date(access.paid_expires_at).getTime() - now) / 86_400_000))
    : null;

  useIdleLogout(Boolean(user), tenant.code);

  async function signOut() {
    await logout();
    router.replace(`/${tenant.code}/login`);
    router.refresh();
  }

  return (
    <header
      className={usesStructure ? "tenant-structure border-b" : "border-b bg-card"}
      aria-label={copy.accountHeader}
    >
      <div
        className={`mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-2 sm:flex-nowrap sm:px-6 ${hasLogo ? "min-h-20" : "min-h-16"}`}
      >
        <Link
          href={`/${tenant.code}/dashboard`}
          aria-label={`${tenant.displayName} dashboard`}
          className="flex shrink-0 items-center gap-2 font-semibold text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {hasLogo ? (
            <TenantLogo className="size-14 rounded-xl object-cover sm:size-16" locale={locale} tenant={tenant.code} />
          ) : (
            <>
              <span className="grid size-9 place-items-center rounded-lg bg-primary text-primary-foreground" aria-hidden="true">
                {tenant.displayName.slice(0, 1)}
              </span>
              <span>{tenant.displayName}</span>
            </>
          )}
        </Link>

        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2 text-sm sm:flex-nowrap">
          {showsAccessReminder && (access?.status === "paid" ? (
            <div
              className="order-last flex w-full items-center justify-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-400/15 px-4 py-1.5 sm:order-none sm:w-auto"
              aria-label={copy.fullAccessActive}
            >
              <span className="flex items-center gap-1.5 whitespace-nowrap font-semibold text-emerald-300">
                <span className="size-2 rounded-full bg-emerald-400" aria-hidden="true" />
                {copy.fullAccessActive}
              </span>
              {daysRemaining !== null && <span className="whitespace-nowrap text-current/70">{copy.daysLeft(daysRemaining)}</span>}
            </div>
          ) : (
            <div
              className="order-last flex w-full min-w-0 items-center justify-between gap-2 rounded-full border border-current/25 bg-background/10 p-1 pl-3 sm:order-none sm:w-auto"
              aria-label={copy.accessReminder}
            >
              <span className="min-w-0 flex-1 truncate font-semibold">
                <span className="sm:hidden">{copy.fullAccessOfferShort}</span>
                <span className="hidden sm:inline">{copy.fullAccessOffer}</span>
              </span>
              {onActivate ? (
                <button type="button" onClick={onActivate} className={buttonVariants({ size: "sm", className: "h-8 shrink-0 rounded-full px-4" })}>
                  {copy.activateAccess}
                </button>
              ) : (
                <Link href="/pricing" className={buttonVariants({ size: "sm", className: "h-8 shrink-0 rounded-full px-4" })}>
                  {copy.activateAccess}
                </Link>
              )}
            </div>
          ))}

          {user && (
            <span className="grid size-9 shrink-0 place-items-center rounded-full bg-secondary font-semibold text-secondary-foreground" aria-hidden="true">
              {initials}
            </span>
          )}
          {user && <span className="hidden whitespace-nowrap font-medium lg:inline">{user.name}</span>}
          <div className="flex shrink-0 items-center gap-1" aria-label={copy.interfaceLanguage}>
            {(["fr", "en"] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => onLocaleChange(value)}
                aria-pressed={locale === value}
                className="rounded px-1.5 py-1 uppercase hover:bg-muted hover:text-foreground aria-pressed:font-bold aria-pressed:text-primary"
              >
                {value}
              </button>
            ))}
          </div>
          {guideHref && (
            <Link
              href={guideHref}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden whitespace-nowrap rounded-lg border px-3 py-2 hover:bg-muted hover:text-foreground xl:inline"
            >
              {copy.guide}
            </Link>
          )}
          {guideHref && (
            <Menu.Root>
              <Menu.Trigger render={<Button type="button" variant="ghost" size="icon" aria-label={copy.menu} className="xl:hidden" />}>
                <MenuIcon />
              </Menu.Trigger>
              <Menu.Portal>
                <Menu.Positioner side="bottom" align="end" sideOffset={8} className="z-50">
                  <Menu.Popup className="min-w-40 rounded-lg border bg-popover p-1 text-sm text-popover-foreground shadow-md">
                    <Menu.LinkItem
                      href={guideHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block cursor-pointer rounded-md px-3 py-2 whitespace-nowrap outline-none data-[highlighted]:bg-muted"
                    >
                      {copy.guide}
                    </Menu.LinkItem>
                  </Menu.Popup>
                </Menu.Positioner>
              </Menu.Portal>
            </Menu.Root>
          )}
          {user && (
            <Button type="button" variant="ghost" size="icon" onClick={signOut} aria-label={copy.logout}>
              <LogOut />
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
